import { useEffect, useState } from "react";
import { useQuickEditRefresh } from "@/hooks/useQuickEditRefresh";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Loader2, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
// pdfReport is dynamically imported at call time to keep jsPDF off the initial bundle.

const TACK_TYPES = ["saddle", "bridle", "girth", "mallet", "helmet", "other"];

export default function TackInventoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "saddle", qty: 1, location: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get("/inventory/tack"); setItems(res.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useQuickEditRefresh(load);

  const submit = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.post("/inventory/tack", { ...form, qty: parseInt(form.qty) || 1 });
      toast.success("Tack item added");
      setOpen(false);
      setForm({ name: "", type: "saddle", qty: 1, location: "", notes: "" });
      load();
    } catch (e) {
      toast.error("Failed to add tack");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    setDeletingId(id);
    try {
      await api.delete(`/inventory/tack/${id}`);
      toast.success(`Removed ${name}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (e) {
      toast.error("Failed to delete");
      throw e;
    } finally {
      setDeletingId(null);
    }
  };

  const exportPdf = async () => {
    try {
      const { downloadReport } = await import("@/lib/pdfReport");
      await downloadReport({
        eyebrow: "INVENTORY",
        title: "Tack Inventory",
        period: "Live count of saddles, bridles, mallets, helmets and other tack",
        filename: "tack-inventory",
        summary: [{ label: "Total items", value: items.length }],
        columns: [
          { header: "Name", dataKey: "name" },
          { header: "Type", dataKey: "type" },
          { header: "Qty", dataKey: "qty", mono: true, halign: "right" },
          { header: "Location", dataKey: "location" },
          { header: "Notes", dataKey: "notes" },
        ],
        rows: items,
      });
    } catch (e) {
      toast.error("Could not generate PDF");
      console.error(e);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tack inventory"
        subtitle={`${items.length} item${items.length === 1 ? "" : "s"}. saddles, bridles, mallets and more`}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={exportPdf}
              disabled={loading || items.length === 0}
              data-testid="tack-download-pdf-button"
            >
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="tack-add-button"><Plus className="h-4 w-4 mr-2" /> Add tack</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add tack item</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. English saddle #2"
                    data-testid="tack-name-input"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="tack-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TACK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quantity</Label>
                  <Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Location</Label>
                  <Input
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="e.g. Stables / Uplands / Tack room"
                    data-testid="tack-location-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Notes</Label>
                  <Input
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="optional"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} disabled={saving} data-testid="tack-submit-button">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />
      <Card className="rounded-xl overflow-hidden">
        <div className="hidden md:block overflow-auto" data-testid="tack-table">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--ap-paper))]">
              <tr className="text-xs uppercase tracking-[0.14em] text-muted-foreground border-b">
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-right px-4 py-3">Qty</th>
                <th className="text-left px-4 py-3">Location</th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">No tack items yet.</td></tr>
              ) : (
                items.map((i) => (
                  <tr key={i.id} className="border-b hover:bg-accent/60" data-testid={`tack-row-${i.id}`}>
                    <td className="px-4 py-3 font-medium">{i.name}</td>
                    <td className="px-4 py-3 text-muted-foreground capitalize">{i.type}</td>
                    <td className="px-4 py-3 text-right font-mono">{i.qty}</td>
                    <td className="px-4 py-3 text-muted-foreground">{i.location || ""}</td>
                    <td className="px-4 py-3 text-right">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={deletingId === i.id}
                            data-testid={`tack-delete-${i.id}`}
                            className="text-muted-foreground hover:text-destructive"
                            aria-label={`Delete ${i.name}`}
                          >
                            {deletingId === i.id
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <Trash2 className="h-4 w-4" />}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove {i.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This deletes the tack item from inventory. The action is logged in the audit trail.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => remove(i.id, i.name)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              data-testid={`tack-delete-confirm-${i.id}`}
                            >
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile tack cards */}
        <div className="md:hidden divide-y" data-testid="tack-cards">
          {loading ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">No tack items yet.</div>
          ) : items.map((i) => (
            <div key={i.id} className="flex items-center gap-3 px-4 py-3" data-testid={`tack-card-${i.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{i.name}</div>
                <div className="text-xs text-muted-foreground capitalize mt-0.5 flex flex-wrap gap-x-2">
                  <span>{i.type}</span>
                  {i.location && <><span>·</span><span>{i.location}</span></>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-lg tabular-nums">{i.qty}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">qty</div>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={deletingId === i.id}
                    data-testid={`tack-delete-mobile-${i.id}`}
                    className="text-muted-foreground hover:text-destructive shrink-0"
                    aria-label={`Delete ${i.name}`}
                  >
                    {deletingId === i.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Trash2 className="h-4 w-4" />}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove {i.name}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This deletes the tack item from inventory. The action is logged in the audit trail.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => remove(i.id, i.name)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Remove
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
